import { useState, PropsWithChildren } from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import {
  Amenity,
  BucketAttachment,
  Building,
  CreateAmenityData,
  UpdateAmenityData,
} from "../../../../graphql/models";

import useAmenityFormStyle from "./amenity_form.style";

import {
  AttachmentsPicker,
  GqlRecordPicker,
  I18NMarkdownEditor,
  I18NTextField,
} from "../../../components";
import { useSnackbarMutation } from "../../../../hooks";
import { Color, ColorPicker, createColor } from "material-ui-color";
import { i18nForFields } from "../../../../i18n";
import {
  BookingInfo,
  BookingInfoServiceTypeEnum,
} from "../../../components/booking/booking_info";

export interface AmenityEditFormProps extends PropsWithChildren<any> {
  value: Amenity | CreateAmenityData | UpdateAmenityData | undefined;
  onValueUpdatedSuccessfully: (v: Amenity) => void;
}

export function AmenityForm(props: AmenityEditFormProps) {
  const { value, onValueUpdatedSuccessfully } = props;
  const classes = useAmenityFormStyle();

  let mutation = MUTATIONS.CREATE_AMENITY;
  if (value && (value as UpdateAmenityData).id) {
    mutation = MUTATIONS.UPDATE_AMENITY;
  }

  const [
    invokeCreateOrUpdateAmenity,
    { loading, data: createOrUpdateAmenityData },
  ] = useSnackbarMutation(mutation);
  const [updated, setUpdated] = useState(false);

  const [i18nFields, setI18nFields] = useState<
    Partial<
      i18nForFields<"name" | "categoryName" | "summaryInfo" | "description">
    >
  >(value?.i18n ?? {});
  // const [name, setName] = useState<string>(value?.name ?? "");
  // const [categoryName, setCategoryName] = useState<string>(
  //   value?.categoryName ?? ""
  // );
  const [building, setBuilding] = useState<Building | undefined>(
    value?.building ?? void 0
  );
  const [airKeyLockId, setAirKeyLockId] = useState<string | undefined>(
    value?.airKeyLockId
  );
  const [sofiaLockTag, setSofiaLockTag] = useState<string | undefined>(
    value?.sofiaLockTag
  );
  const [color, setColor] = useState<Color | undefined>(
    value?.color ? createColor(value.color) : void 0
  );
  const [active] = useState<boolean>(value?.active ?? true);
  const [alertEmail, setAlertEmail] = useState<string | undefined>(
    value?.alertEmail
  );
  const [isGuestParking, setIsGuestParking] = useState(
    value?.isGuestParking ?? false
  );
  const [isOnboardingAppointment, setIsOnboardingAppointment] = useState(
    value?.isOnboardingAppointment ?? false
  );
  const [liability, setLiability] = useState<BucketAttachment | undefined>(
    value?.liability
  );
  const [bookingInfo, setBookingInfo] = useState(
    value?.bookingInfo ?? {
      periods: [],
      durations: [],
      intervalBetweenStartsInMinutes: 0,
      defaultAdvanceInMinutes: 0,
    }
  );

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdateAmenity({
      variables: {
        amenityData: {
          id: (value as UpdateAmenityData)?.id,
          building,
          active,
          color: color ? `#${color.hex}` : void 0,
          i18n: i18nFields,
          airKeyLockId,
          sofiaLockTag,
          alertEmail,
          isGuestParking,
          isOnboardingAppointment,
          liability,
          bookingInfo,
        },
      },
    });
  };

  if (createOrUpdateAmenityData && !updated) {
    onValueUpdatedSuccessfully(createOrUpdateAmenityData.createSystemAmenity);
    setUpdated(true);
  }

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateAmenityData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                className={classes.textField}
                label="Nome"
                variant="outlined"
                i18nFieldName="name"
                value={i18nFields}
                onChange={setI18nFields}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <I18NTextField
                className={classes.textField}
                label="Categoria"
                variant="outlined"
                i18nFieldName="categoryName"
                value={i18nFields}
                onChange={setI18nFields}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <GqlRecordPicker
                className={classes.textField}
                multiple={false}
                value={building}
                getOptionLabel={(record) => record.name}
                placeHolder={"Palazzo"}
                renderOption={(record) => record.name}
                onValueChange={(value) => setBuilding(value)}
                query={QUERIES.BUILDINGS("light")}
                queryResultDataField={"buildings"}
              />
            </Grid>
            <Grid item sm={12}>
              <I18NTextField
                className={classes.textField}
                label="Testo informazioni utili nel riepilogo della prenotazione"
                variant="outlined"
                i18nFieldName="summaryInfo"
                value={i18nFields}
                onChange={setI18nFields}
              />
            </Grid>

            <Grid item sm={12}>
              <I18NMarkdownEditor
                label="Descrizione dell'amenity (solo palazzo Duomo18)"
                i18nFieldName="description"
                value={i18nFields}
                onChange={setI18nFields}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h3">PDF manleva</Typography>
              <p>
                Se viene caricato un documento, per prenotare il servizio
                l'utente deve accettare i contenuti prensenti nel file
              </p>
              <AttachmentsPicker
                defaultValue={liability ? [liability] : void 0}
                onUpdated={(v) => setLiability(v[0])}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                className={classes.textField}
                label="ID Serratura di AirKey"
                variant="outlined"
                value={airKeyLockId}
                onChange={(e) => setAirKeyLockId(e.target.value.trim())}
              ></TextField>
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                className={classes.textField}
                label="Tag Serratura Sofia Lock"
                variant="outlined"
                value={sofiaLockTag}
                onChange={(e) => setSofiaLockTag(e.target.value.trim())}
              ></TextField>
            </Grid>
            <Grid item sm={3} xs={12}>
              <ColorPicker value={color} onChange={setColor} />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                className={classes.textField}
                label="Indirizzo email da avvisare per nuove prenotazioni"
                variant="outlined"
                value={alertEmail}
                onChange={(evt) => setAlertEmail(evt.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Checkbox
                checked={isGuestParking}
                onChange={(e, value) => setIsGuestParking(value)}
              ></Checkbox>{" "}
              Questa amenity &egrave; un <i>Guest Parking</i>
            </Grid>
            <Grid item xs={12}>
              <Checkbox
                checked={isOnboardingAppointment}
                onChange={(e, value) => setIsOnboardingAppointment(value)}
              ></Checkbox>{" "}
              Questa amenity &egrave; dedicata all' <i>Onboarding</i>
            </Grid>
            <Grid item xs={12}>
              <BookingInfo
                value={bookingInfo}
                onChange={setBookingInfo}
                type={BookingInfoServiceTypeEnum.AMENITY}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Button
                className={classes.textField}
                type="submit"
                variant="contained"
                color="primary"
              >
                {!loading ? "Salva" : <CircularProgress />}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}
